import type { JSX } from "react";
import type { EntityRendererProps } from "../../runtime/contracts/agent";
import { BaseEntityCard } from "../../modules/_base/BaseEntityCard";

interface DiffEntry {
  readonly field: string;
  readonly before: unknown;
  readonly after: unknown;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "∅";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

function toDiffEntries(changed: unknown): readonly DiffEntry[] {
  if (!changed || typeof changed !== "object" || Array.isArray(changed)) return [];
  return Object.entries(changed as Record<string, unknown>)
    .filter(([, entry]) => entry !== null && typeof entry === "object")
    .map(([field, entry]) => {
      const e = entry as Record<string, unknown>;
      return { field, before: e.before, after: e.after };
    });
}

/**
 * Renders a `kind:"updated"` envelope inside the standard BaseEntityCard
 * shell (icon + clickable entity link), with a `field: before → after`
 * table. Empty `changed` (no-op update) renders a "no changes" line so the
 * static attachment never collapses to nothing.
 */
export function UpdatedDiffCard(props: EntityRendererProps): JSX.Element {
  const entries = toDiffEntries(props.data.changed);

  return (
    <BaseEntityCard {...props}>
      <div className="min-w-0 flex-1">
        {entries.length === 0 ? (
          <span className="block text-[12px] text-content-tertiary">No changes</span>
        ) : (
          <table className="w-full text-[12px]">
            <tbody>
              {entries.map((e) => (
                <tr key={e.field}>
                  <td className="pr-2 font-medium text-content-secondary align-top">
                    {e.field}
                  </td>
                  <td className="pr-2 text-content-tertiary align-top line-through">
                    {formatValue(e.before)}
                  </td>
                  <td className="text-content align-top">→ {formatValue(e.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </BaseEntityCard>
  );
}
